import React, { useEffect, useState, useContext } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { collection, addDoc, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebaseConfig';
import { AppContext } from '../AppContext';

function AddCustomer({ open, onClose, onAdd, editMode = false, customerToEdit = {}, setSuccess, setError }) {
  const { user, userData } = useContext(AppContext);
  const activeFarmId = userData?.activeFarmId;

  const [newCustomer, setNewCustomer] = useState({
    businessName: '',
    pointOfcontact: '',
    address: '',
    contactNumber: '',
    contactEmail: '',
    notes: '',
    paperless: false,
    cod: false,
  });

  const [touched, setTouched] = useState({});
  const [formSubmitted, setFormSubmitted] = useState(false);

  useEffect(() => {
    if (open) {
      if (editMode && customerToEdit) {
        setNewCustomer(customerToEdit);
      } else {
        setNewCustomer({
          businessName: '',
          pointOfcontact: '',
          address: '',
          contactNumber: '',
          contactEmail: '',
          notes: '',
          paperless: false,
          cod: false,
        });
      }
    }
  }, [open, editMode, customerToEdit]);

  const handleSave = async () => {
    setFormSubmitted(true);
    // Validate required fields
    if (!newCustomer.businessName || !newCustomer.address) return;

    try {
      if (!user || !user.uid) {
        throw new Error('User not defined');
      }
      if (!activeFarmId) {
        throw new Error('activeFarmId is missing');
      }

      if (editMode && customerToEdit?.id) {
        // Update existing
        await updateDoc(
          doc(db, 'farms', activeFarmId, 'customers', customerToEdit.id),
          newCustomer
        );
        setSuccess?.('Customer updated successfully');
      } else {
        // Create new
        await addDoc(
          collection(db, 'farms', activeFarmId, 'customers'),
          newCustomer
        );
        setSuccess?.('Customer added successfully');
      }

      if (onAdd) onAdd(newCustomer);
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving customer', error);
      setError?.('Failed to save customer');
    }
  };


  const handleCloseDialog = () => {
    setFormSubmitted(false);
    setTouched({});
    onClose();
  };

  const handleBlur = (field) => {
    setTouched({ ...touched, [field]: true });
  };

  const isError = (field) => {
    return !newCustomer[field] && (touched[field] || formSubmitted);
  };

  const handleDialogKeyDown = (e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      handleCloseDialog();
      return;
    }

    if (e.key !== 'Enter') return;
    if (e.defaultPrevented || e.shiftKey || e.ctrlKey || e.altKey || e.metaKey) return;

    const target = e.target;
    const tag = target?.tagName?.toLowerCase();
    if (tag === 'textarea' || target?.isContentEditable) return;
    if (target?.closest?.('[role="listbox"], [role="menu"]')) return;

    e.preventDefault();
    handleSave();
  };

  return (
    <Dialog open={open} onClose={handleCloseDialog} onKeyDown={handleDialogKeyDown}>
      <DialogTitle>{editMode ? 'Edit Customer' : 'Add New Customer'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          {/* Required fields (keep top) */}
          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Business Name"
              fullWidth
              required
              value={newCustomer.businessName}
              onChange={(e) => setNewCustomer({ ...newCustomer, businessName: e.target.value })}
              onBlur={() => handleBlur('businessName')}
              error={isError('businessName')}
              helperText={isError('businessName') ? 'This field is required' : ''}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Business Address"
              fullWidth
              required
              value={newCustomer.address}
              onChange={(e) => setNewCustomer({ ...newCustomer, address: e.target.value })}
              onBlur={() => handleBlur('address')}
              error={isError('address')}
              helperText={isError('address') ? 'This field is required' : ''}
            />
          </Grid>

          {/* Pair up on desktop */}
          <Grid item xs={12} sm={12}>
            <TextField
              margin="dense"
              label="Point of Contact"
              fullWidth
              placeholder="e.g John Smith"
              value={newCustomer.pointOfcontact}
              onChange={(e) => setNewCustomer({ ...newCustomer, pointOfcontact: e.target.value })}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              label="Contact Number"
              fullWidth
              value={newCustomer.contactNumber}
              onChange={(e) => {
                const val = e.target.value;
                // Allow empty string or only digits
                if (/^\d*$/.test(val)) {
                  setNewCustomer({ ...newCustomer, contactNumber: val });
                }
              }}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              margin="dense"
              label="Contact Email"
              fullWidth
              value={newCustomer.contactEmail}
              onChange={(e) => setNewCustomer({ ...newCustomer, contactEmail: e.target.value })}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              margin="dense"
              label="Notes"
              fullWidth
              multiline
              minRows={2}
              value={newCustomer.notes}
              onChange={(e) => setNewCustomer({ ...newCustomer, notes: e.target.value })}
            />
          </Grid>

          {/* Checkboxes row */}
          <Grid item xs={12}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm="auto">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newCustomer.cod || false}
                      onChange={(e) => setNewCustomer({ ...newCustomer, cod: e.target.checked })}
                    />
                  }
                  label="Cash on delivery"
                />
              </Grid>

              <Grid item xs={12} sm="auto">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={newCustomer.paperless || false}
                      onChange={(e) => setNewCustomer({ ...newCustomer, paperless: e.target.checked })}
                    />
                  }
                  label="Paperless"
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="text" sx={{ color: (theme) => theme.palette.primary.light }} onClick={handleCloseDialog}>
          Cancel
        </Button>
        <Button variant="contained" color="secondary" onClick={handleSave}>{editMode ? 'Update' : 'Add'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AddCustomer;
